export type BillingInvoiceItem = {
    id: string;
    status: string;
    valueBrl: number;
    dueDate?: string | null;
    paidAt?: string | null;
    invoiceUrl?: string | null;
};

export type BillingProrationPreview = {
    daysRemaining: number;
    daysInPeriod: number;
    upgradeChargeBrl?: number | null;
    paymentMethodSwitchSettlementBrl: number;
    downgradeTargetPlanId?: string | null;
};

export type BillingSubscriptionSummary = {
    subscriptionPlan: string;
    currentPlanId?: string | null;
    planName: string;
    subscriptionEndsAt?: string | null;
    subscriptionCancelledAt?: string | null;
    status?: string | null;
    paymentMethod?: string | null;
    pendingPlanId?: string | null;
    periodEndDate?: string | null;
    nextChargeDate?: string | null;
    /** Present when H1: admin-extended access past paid period while renewal cancelled. */
    mivoTeamExtensionEndsAt?: string | null;
    billingProvider?: string | null;
    devSimulatePixAvailable?: boolean;
    devFakeBillingAvailable?: boolean;
    basicTrialConsumed?: boolean;
    canStartBasicTrial?: boolean;
    canChangePlan?: boolean;
    canChangePaymentMethod?: boolean;
    prorationPreview?: BillingProrationPreview | null;
    referralCheckoutPending?: boolean;
    referralRefundEligible?: boolean;
    referralRefundWindowEndsAt?: string | null;
};

export type CancelTrialResponse = {
    accountAccessMode: "deactivated_billing_only";
    planId: string;
    clinicalAccessBlocked: true;
    effectiveAt: string;
};

export type CancelSubscriptionResponse = {
    status: string;
    subscriptionEndsAt: string;
    warnings: string[];
};

export type SubscriptionCheckoutMode = "checkout" | "manage" | "payment-method";

/**
 * Solo therapist checkout / change-payment-method Pix Automático.
 * Set `VITE_SUBSCRIPTION_PIX_CHECKOUT_ENABLED=true` when Asaas Pix Automático is active.
 * @see docs/domains/billing/pixCheckoutUx/spec.md
 */
export const subscriptionPixCheckoutEnabled =
    import.meta.env.VITE_SUBSCRIPTION_PIX_CHECKOUT_ENABLED === "true";

/** Change-payment UI (Seu plano button + manage checkbox) requires Pix Automático. */
export function canShowChangePaymentMethod(
    billing?: Pick<BillingSubscriptionSummary, "canChangePaymentMethod"> | null,
): boolean {
    return subscriptionPixCheckoutEnabled && Boolean(billing?.canChangePaymentMethod);
}

export function isManageableSubscription(summary?: BillingSubscriptionSummary | null): boolean {
    return Boolean(
        summary?.canChangePlan &&
        (summary.status === "active" || summary.status === "cancel_at_period_end"),
    );
}

export function formatBrl(value: number): string {
    return `R$ ${value.toFixed(2).replace(".", ",")}`;
}

export function shouldUseClinicBilling(user?: {
    activeClinicId?: string | null;
    isMainManager?: boolean | null;
}): boolean {
    return Boolean(user?.activeClinicId && user?.isMainManager);
}
